import { getTexts } from '@twnel/web-components';
import { SUCCESS_TYPE, ERROR_TYPE } from 'utils/toastDispacher';
import { toastDispacher } from 'utils';
import { botspecsActions } from '../reducer';
import { getAllBotspecs, getGlobalFunctions as getGlobalFunctionsSelector } from '../selectors';
import { updateBotspecsVariable } from './spec_parser';
import { throwNetworkError } from './chatbots';
import logger from 'utils/logger';

const GLOBAL_FUNCTIONS_GETTER = 'GLOBAL_FUNCTIONS_GETTER';
const GLOBAL_FUNCTION_CREATION = 'GLOBAL_FUNCTION_CREATION';
const GLOBAL_FUNCTION_UPDATE = 'GLOBAL_FUNCTION_UPDATE';
const GLOBAL_FUNCTION_DELETION = 'GLOBAL_FUNCTION_DELETION';


/**
 * Verifica si un botspec específico usa una función global determinada
 */
const botspecUsesSpecificGlobalFunction = (botspec, functionName) => {
  if (!botspec || !botspec.spec_json) {
    return false;
  }
  const specJsonString = JSON.stringify(botspec.spec_json);

  // Buscar referencias a la función global en diferentes formatos:
  // 1. Formato antiguo: "function":"nombre_funcion"
  // 2. Formato nuevo: "function":"global.nombre_funcion"
  // 3. En el objeto functions.global
  return specJsonString.includes(`"function":"${functionName}"`)
    || specJsonString.includes(`"function":"global.${functionName}"`)
    || specJsonString.includes(`functions.global.${functionName}`);
};

const updateBotspecsWithGlobalFunctions = (functionName) => (dispatch, getState) => {
  const state = getState();
  const allBotspecs = getAllBotspecs(state);
  const botspecsToUpdate = Object.keys(allBotspecs)
    .map((key) => allBotspecs[key])
    .filter((botspec) => botspecUsesSpecificGlobalFunction(botspec, functionName));

  if (botspecsToUpdate.length === 0) {
    return Promise.resolve();
  }

  const updatePromises = botspecsToUpdate.map(
    (botspec) => dispatch(updateBotspecsVariable(botspec)),
  );
  return Promise.all(updatePromises).catch((error) => {
    logger.error('Error updating botspecs with global functions:', error);
  });
};

/**
 * Obtener todas las funciones globales de la compañía
 */
export const fetchGlobalFunctions = (forceRefresh = false) => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const texts = getTexts(getState());
  const state = getState();
  const cachedFunctions = getGlobalFunctionsSelector(state);

  if (!forceRefresh && Object.keys(cachedFunctions).length > 0) {
    return Promise.resolve(cachedFunctions);
  }

  dispatch(botspecsActions.loadingGlobalFunctions(true));

  return api.functions.list()
    .then((functions) => {
      // Convertir el array de funciones a un objeto con el nombre como key
      const functionsObject = functions.reduce((acc, func) => {
        acc[func.name] = func;
        return acc;
      }, {});

      dispatch(botspecsActions.updateGlobalFunctions(functionsObject));

      return functionsObject;
    })
    .catch((error) => {
      dispatch(botspecsActions.loadingGlobalFunctions(false));
      toastDispacher({ actionId: GLOBAL_FUNCTIONS_GETTER, texts, type: ERROR_TYPE });
      throwNetworkError(error);
    });
};

/**
 * Crear una nueva función global
 */
export const createGlobalFunction = (functionName, functionData) => (
  dispatch, getState, getContext,
) => {
  const { api } = getContext();
  const texts = getTexts(getState());

  return api.functions.create(functionName, functionData)
    .then((newFunction) => {
      toastDispacher({ actionId: GLOBAL_FUNCTION_CREATION, texts, type: SUCCESS_TYPE });
      dispatch(fetchGlobalFunctions(true));
      dispatch(updateBotspecsWithGlobalFunctions(functionName));
      return newFunction;
    })
    .catch((error) => {
      toastDispacher({ actionId: GLOBAL_FUNCTION_CREATION, texts, type: ERROR_TYPE });
      throwNetworkError(error);
    });
};

/**
 * Actualizar una función global existente
 */
export const updateGlobalFunction = (functionName, functionData) => (
  dispatch, getState, getContext,
) => {
  const { api } = getContext();
  const texts = getTexts(getState());

  return api.functions.update(functionName, functionData)
    .then((updatedFunction) => {
      toastDispacher({ actionId: GLOBAL_FUNCTION_UPDATE, texts, type: SUCCESS_TYPE });
      dispatch(fetchGlobalFunctions(true));
      dispatch(updateBotspecsWithGlobalFunctions(functionName));
      return updatedFunction;
    })
    .catch((error) => {
      toastDispacher({ actionId: GLOBAL_FUNCTION_UPDATE, texts, type: ERROR_TYPE });
      throwNetworkError(error);
    });
};

/**
 * Eliminar una función global
 */
export const deleteGlobalFunction = (functionName) => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const texts = getTexts(getState());

  return api.functions.delete(functionName)
    .then(() => {
      toastDispacher({ actionId: GLOBAL_FUNCTION_DELETION, texts, type: SUCCESS_TYPE });
      dispatch(fetchGlobalFunctions(true));
      dispatch(updateBotspecsWithGlobalFunctions(functionName));
      return true;
    })
    .catch((error) => {
      toastDispacher({ actionId: GLOBAL_FUNCTION_DELETION, texts, type: ERROR_TYPE });
      throwNetworkError(error);
    });
};
